/**
 * Checks if a user has permission to access a module or a specific process within it.
 * 
 * @param {Object} user - The user object from the global state/context.
 * @param {string} module - The module identifier (e.g., 'onboarding', 'usuarios').
 * @param {string|number} processId - Optional. The specific process or document ID.
 * @returns {boolean} - True if access is granted.
 */
const normalizeToken = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_");

const MODULE_ALIASES = {
  gestion_compras: "compras",
  logistica: "compras",
  gestion_ventas: "ventas",
  gestion_tesoreria: "tesoreria",
  gestion_contable: "contabilidad",
  contable: "contabilidad",
  planilla: "planillas",
};

const PROCESS_ALIASES = {
  usuarios: {
    EDITAR: "USUARIOS_EDITAR",
    USUARIO_EDITAR: "USUARIOS_EDITAR",
    DESACTIVAR: "USUARIOS_DESACTIVAR",
    ELIMINAR_MASIVO: "USUARIOS_ELIMINAR_MASIVO",
    RESETEAR_CLAVE: "USUARIOS_RESETEAR_CLAVE",
    RESET_SEGURIDAD: "USUARIOS_RESETEAR_CLAVE",
    USUARIOS_MASIVOS: "USUARIOS_MASIVOS_CARGA",
    CUMPLEANOS: "ALERTAS_MASIVAS",
  },
  compras: {
    ORDEN: "ORDENES",
    ORDENES_COMPRA: "ORDENES",
    PROVEEDOR: "PROVEEDORES",
    ITEMS: "CATALOGO",
    CATALOGO_ITEMS: "CATALOGO",
  },
  ventas: {
    ORDEN: "ORDENES",
    ORDENES_VENTA: "ORDENES",
    COMPROBANTE: "COMPROBANTES",
    FACTURA: "COMPROBANTES",
    FACTURAS: "COMPROBANTES",
    BOLETA: "COMPROBANTES",
    BOLETAS: "COMPROBANTES",
  },
  tesoreria: {
    DIA_CALLE: "DIAS_CALLE",
    DIAS_CALLE: "DIAS_CALLE",
  },
  contabilidad: {
    FACTURA: "COMPRAS",
    FACTURAS: "COMPRAS",
    CXP: "PAGOS",
    CUENTAS_PAGAR: "PAGOS",
    CUENTAS_COBRAR: "COBROS",
  },
  planillas: {
    // Compatibilidad entre permisos históricos y nuevos nombres en UI/rutas
    PLANILLAS: "LISTA_PLANILLAS",
    LISTA: "LISTA_PLANILLAS",
    REGIMEN: "REGIMEN_LABORAL",
    CONFIG_REGIMEN: "REGIMEN_LABORAL",
    CONFIG_PLANILLA: "CONFIGURACION",
    NUEVA_OCURRENCIA: "OCURRENCIAS",
    EMPLEADOS_MASIVOS: "EMPLEADOS",
  },
};

const resolveModuleKey = (module) => {
  const raw = String(module || "").trim().toLowerCase();
  return MODULE_ALIASES[raw] || raw;
};

const resolveProcessKey = (module, processId) => {
  if (processId === null || processId === undefined) {
    return null;
  }

  const process = normalizeToken(processId);
  const aliases = PROCESS_ALIASES[resolveModuleKey(module)] || {};
  return aliases[process] || process;
};

const hasProcessPermission = (modulePerms, module, processId) => {
  const requested = resolveProcessKey(module, processId);
  return modulePerms.some((p) => resolveProcessKey(module, p) === requested);
};

export function checkPermission(user, module, processId = null) {
  if (!user) return false;
  if (user.rol === 'superAdmin') return true;

  const moduleKey = resolveModuleKey(module);
  const processKey = resolveProcessKey(moduleKey, processId);

  // Todos los usuarios autenticados tienen acceso a la Mesa de Ayuda (HELP_DESK) y al módulo atencion
  if (moduleKey === "atencion" && (processKey === "HELP_DESK" || processKey === null)) {
    return true;
  }
  const hasGranularPermsObject =
    user.permisos &&
    typeof user.permisos === "object" &&
    !Array.isArray(user.permisos);

  // Safety: superAdmin should ALWAYS be able to see the 'usuarios' module and user list to avoid self-lockouts
  if (user.rol === 'superAdmin' && moduleKey === 'usuarios' && (processKey === null || processKey === 'USUARIOS')) {
      return true;
  }

  // If the user has a granular 'permisos' object
  // Support legacy key variants (e.g. "Planillas" vs "planillas")
  let hasModulePerms = false;
  let modulePerms;
  if (hasGranularPermsObject) {
    if (Object.prototype.hasOwnProperty.call(user.permisos, moduleKey)) {
      hasModulePerms = true;
      modulePerms = user.permisos[moduleKey];
    } else {
      const legacyModuleKey = Object.keys(user.permisos).find(
        (k) => resolveModuleKey(k) === moduleKey
      );
      if (legacyModuleKey) {
        hasModulePerms = true;
        modulePerms = user.permisos[legacyModuleKey];
      }
    }
  }

  if (hasModulePerms) {

    // Boolean true means full access to the module
    if (modulePerms === true || (Array.isArray(modulePerms) && modulePerms.includes('*'))) {
      return true;
    }

    // Boolean false means explicit denial
    if (modulePerms === false) {
      return false;
    }

    // If an array and a processId is provided, check if it's included
    if (Array.isArray(modulePerms)) {
      if (processKey !== null) {
        return hasProcessPermission(modulePerms, moduleKey, processKey);
      }
      
      // If no processId, allow access only if the array is not empty
      return modulePerms.length > 0;
    }

    // Legacy compatibility: object map of process => boolean
    // Example: { EMPLEADOS: true, BOLETAS: false }
    if (modulePerms && typeof modulePerms === "object") {
      const entries = Object.entries(modulePerms);
      const allowedProcesses = entries
        .filter(([, v]) => v === true)
        .map(([k]) => resolveProcessKey(moduleKey, k));

      if (processKey !== null) {
        return allowedProcesses.includes(processKey);
      }

      return allowedProcesses.length > 0;
    }
  }

  // STRICT GRANULAR RULE:
  // If user has granular permissions configured, do not fallback to legacy role rules
  // when the module is missing in permisos. This prevents unintended full access.
  if (hasGranularPermsObject) {
    return false;
  }

  // Fallback to legacy role-based permissions if no matched granular perm
  // This ensures backward compatibility
  const legacyRoles = {
    onboarding: ['admin', 'asistencia', 'supervisor'],
    usuarios: ['admin'],
    centro_costos: ['admin'],
    planillas: ['admin'],
    compras: ['admin'],
    ventas: ['admin'],
    tesoreria: ['admin'],
    contabilidad: ['admin'],
    asistencia: ['admin', 'asistencia', 'supervisor'],
    ausencias: ['admin', 'asistencia', 'supervisor'],
    epps: ['admin', 'almacenero'],
    reclutamiento: ['admin', 'reclutador'],
    atencion: ['admin']
  };

  // STRICT RULE: If a specific processId is requested, do NOT fallback to legacy roles.
  // This forces new features to be strictly granular.
  if (processKey !== null) {
    return false;
  }

  if (legacyRoles[moduleKey] && legacyRoles[moduleKey].includes(user.rol)) {
    return true;
  }

  return false;
}

export default checkPermission;
